// Members table view (memberships-settings.md, List archetype §4c): columns +
// PaginatedTableSection + the shared ConfirmModal remove confirm (§4a).
// Presentational — data, authority, and mutations arrive as props; the only
// local state is which membership the remove confirm targets.

import { useState } from "react";

import type { ColumnDef } from "@tanstack/react-table";

import type { components } from "../../api/schema";
import { formatDateTime } from "../../lib/format";
import type { ListQueryResult } from "../../lib/useListQuery";
import {
  Badge,
  Button,
  Copyable,
  EmptyState,
  InlineError,
  Select,
  Stack,
  Text,
} from "../../ui";
import { ConfirmModal } from "../shared/ConfirmModal";
import { PaginatedTableSection } from "../shared/PaginatedTableSection";
import { DateCell } from "../shared/DateCell";
import { UserLabel, userDisplayName } from "../shared/userDirectory";

type MemberRole = components["schemas"]["MemberRole"];
type Membership = components["schemas"]["MembershipResponse"];
type User = components["schemas"]["UserResponse"];

// The remove confirm's target — carries the resolved labels so the Modal can
// name the exact member/team without re-resolving from the row.
interface RemoveTarget {
  id: string;
  teamLabel: string;
  userLabel: string;
}

export interface MembershipPatchInput {
  id: string;
  member_role: MemberRole;
}

export interface MembershipsTableProps {
  canManage: boolean;
  deleteErrorMembershipId?: string;
  deleteErrorMessage?: string;
  deletingMembershipId?: string;
  // Members-directory (`?ids=`) resolution failure — rendered beside the
  // table (rows degrade to short ids), never replacing it.
  directoryErrorMessage?: string;
  grantableRoles: MemberRole[];
  memberships: ListQueryResult<Membership>;
  // Empty-state CTA → opens the Add-member drawer (`?new=1`).
  onAddMember: () => void;
  onDeleteMembership: (id: string) => Promise<void>;
  onPatchMembershipRole: (input: MembershipPatchInput) => void;
  onResetDeleteMembership: () => void;
  patchingMembershipId?: string;
  teamsById: ReadonlyMap<string, string>;
  usersById: ReadonlyMap<string, User>;
}

function membershipColumns({
  canManage,
  deletingMembershipId,
  grantableRoles,
  onPatchMembershipRole,
  onStartRemoveMembership,
  patchingMembershipId,
  teamsById,
  usersById,
}: Pick<
  MembershipsTableProps,
  | "canManage"
  | "deletingMembershipId"
  | "grantableRoles"
  | "onPatchMembershipRole"
  | "patchingMembershipId"
  | "teamsById"
  | "usersById"
> & {
  onStartRemoveMembership: (target: RemoveTarget) => void;
}): Array<ColumnDef<Membership>> {
  // UI-LAW §4 DataTable / §4b.4 facet uniformity: sortable-value columns carry
  // accessors; `member_role`/`status` are the low-cardinality facets. The
  // Team column stays facet-free — the root `?team=` working-team param
  // (the sidebar team selector) is the filter, and a client facet must never
  // duplicate a URL filter.
  return [
    {
      header: "Member",
      id: "member",
      accessorFn: (membership) => {
        const user = usersById.get(membership.app_user_id);
        return user ? userDisplayName(user) : membership.app_user_id;
      },
      cell: ({ row }) => (
        <UserLabel
          compact
          fallbackId={row.original.app_user_id}
          user={usersById.get(row.original.app_user_id)}
        />
      ),
    },
    {
      header: "Team",
      cell: ({ row }) => {
        const displayName = teamsById.get(row.original.team_id);
        return displayName ? (
          <Text as="span">{displayName}</Text>
        ) : (
          <Copyable label="Team ID" value={row.original.team_id} />
        );
      },
    },
    {
      header: "Role",
      accessorKey: "member_role",
      cell: ({ row }) => {
        const membership = row.original;
        const rowBusy =
          patchingMembershipId === membership.id ||
          deletingMembershipId === membership.id;

        const user = usersById.get(membership.app_user_id);
        const userLabel = user ? userDisplayName(user) : membership.app_user_id;
        // Ceiling-aware (docs/05 §6.4): only offer roles at/below the caller's
        // authority, and never re-grade a member already above that ceiling.
        const roleEditable =
          canManage &&
          membership.deleted_at == null &&
          grantableRoles.includes(membership.member_role);

        return roleEditable ? (
          <Stack gap={1}>
            <Select
              disabled={rowBusy || membership.is_last_owner}
              label={`Role for ${userLabel}`}
              size="sm"
              value={membership.member_role}
              onChange={(event) => {
                onPatchMembershipRole({
                  id: membership.id,
                  member_role: event.currentTarget.value as MemberRole,
                });
              }}
            >
              {grantableRoles.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </Select>
            {membership.is_last_owner ? (
              <Text as="span" variant="muted">
                Add another owner before changing this role.
              </Text>
            ) : null}
          </Stack>
        ) : (
          <Stack gap={1}>
            <Badge>{membership.member_role}</Badge>
          </Stack>
        );
      },
    },
    {
      header: "Since",
      accessorKey: "created_at",
      cell: ({ row }) => <DateCell value={row.original.created_at} />,
    },
    {
      header: "Status",
      id: "status",
      accessorFn: (membership) =>
        membership.deleted_at == null ? "active" : "removed",
      cell: ({ row }) =>
        row.original.deleted_at ? (
          <Text as="span" variant="muted">
            removed {formatDateTime(row.original.deleted_at)}
          </Text>
        ) : (
          <Text as="span">active</Text>
        ),
    },
    {
      header: "Actions",
      cell: ({ row }) => {
        const membership = row.original;

        if (!canManage || membership.deleted_at != null) {
          return (
            <Text as="span" variant="muted">
              —
            </Text>
          );
        }

        const user = usersById.get(membership.app_user_id);
        const userLabel = user ? userDisplayName(user) : membership.app_user_id;
        const teamLabel =
          teamsById.get(membership.team_id) ?? membership.team_id;

        const isLastOwner = membership.is_last_owner;

        return (
          <Stack gap={1}>
            <Button
              disabled={
                isLastOwner ||
                deletingMembershipId != null ||
                patchingMembershipId === membership.id
              }
              size="sm"
              type="button"
              variant="danger"
              onClick={() => {
                onStartRemoveMembership({
                  id: membership.id,
                  teamLabel,
                  userLabel,
                });
              }}
            >
              Remove
            </Button>
            {isLastOwner ? (
              <Text as="span" variant="muted">
                Add another owner before removing this membership.
              </Text>
            ) : null}
          </Stack>
        );
      },
    },
  ];
}

export function MembershipsTable({
  canManage,
  deleteErrorMembershipId,
  deleteErrorMessage,
  deletingMembershipId,
  directoryErrorMessage,
  grantableRoles,
  memberships,
  onAddMember,
  onDeleteMembership,
  onPatchMembershipRole,
  onResetDeleteMembership,
  patchingMembershipId,
  teamsById,
  usersById,
}: MembershipsTableProps) {
  const [removeTarget, setRemoveTarget] = useState<RemoveTarget | null>(null);

  const closeRemove = (): void => {
    onResetDeleteMembership();
    setRemoveTarget(null);
  };

  const confirmRemove = async (id: string): Promise<void> => {
    try {
      await onDeleteMembership(id);
      setRemoveTarget(null);
    } catch {
      // The parent mutation owns error presentation; the confirm stays open
      // (removeTarget untouched) so the modal can surface the error inline.
    }
  };

  const columns = membershipColumns({
    canManage,
    deletingMembershipId,
    grantableRoles,
    onPatchMembershipRole,
    onStartRemoveMembership: (target) => {
      onResetDeleteMembership();
      setRemoveTarget(target);
    },
    patchingMembershipId,
    teamsById,
    usersById,
  });

  const isRemoving =
    removeTarget != null && deletingMembershipId === removeTarget.id;
  const removeErrorMessage =
    removeTarget != null &&
    deleteErrorMembershipId === removeTarget.id &&
    deleteErrorMessage
      ? deleteErrorMessage
      : null;

  return (
    <Stack fill gap={3}>
      {directoryErrorMessage ? (
        <InlineError>{directoryErrorMessage}</InlineError>
      ) : null}
      <PaginatedTableSection<Membership>
        ariaLabel="Members"
        columns={columns}
        emptyState={
          <EmptyState
            action={
              canManage ? (
                <Button type="button" onClick={onAddMember}>
                  Add member
                </Button>
              ) : undefined
            }
            title="No members"
          />
        }
        enableSorting
        errorMessage={memberships.errorMessage}
        getRowId={(membership) => membership.id}
        hasNextPage={memberships.hasNextPage}
        isFetchingNextPage={memberships.isFetchingNextPage}
        isPending={memberships.isPending}
        items={memberships.items}
        listStateKey={memberships.listStateKey}
        skeletonLabel="Loading members"
        virtual={{ fill: true }}
        onLoadMore={memberships.fetchNextPage}
      />
      {removeTarget ? (
        <ConfirmModal
          ariaLabel="Remove member?"
          confirmLabel="Remove member"
          errorMessage={removeErrorMessage}
          isPending={isRemoving}
          pendingLabel="Removing..."
          sentence={
            `Remove ${removeTarget.userLabel} from ${removeTarget.teamLabel}? ` +
            "This removes the membership."
          }
          onCancel={closeRemove}
          onConfirm={() => {
            void confirmRemove(removeTarget.id);
          }}
        />
      ) : null}
    </Stack>
  );
}
